import { Outlet, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { notifications } from '@mantine/notifications';

import useRetrieveAutomation from '@/hooks/api/automations/useRetrieveAutomation.ts';
import { useAutomationSSE } from '@/hooks/api/sse/useAutomationSSE.ts';
import { EditVersionProvider } from '@/context/EditVersionContext/EditVersionProvider.tsx';
import { StepProvider } from '@/context/StepContext/StepProvider.tsx';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import { AutomationProvider } from '@/context/AutomationContext/AutomationProvider.tsx';
import { useViewDataset } from '@/hooks/useViewDataset.ts';
import LazyLoadErrorBoundary from '@/components/LazyLoadErrorBoundary.tsx';

const DatasetViewerModal = lazy(() => import('@/layouts/DataViewer/DataViewerModal.tsx'));
const E2E_AUTH_BYPASS =
  import.meta.env.MODE === 'e2e' && import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';

const AutomationSSE = () => {
  useAutomationSSE();
  return null;
};

export default function AutomationLayout() {
  const navigate = useNavigate();

  const { data: automation, error: automationError } = useRetrieveAutomation();
  const { projectId, setProjectId } = useOrgProject();
  const { datasetModalId } = useViewDataset();

  useEffect(() => {
    if (automation?.project.id && automation?.project.id !== projectId) {
      setProjectId(automation.project.id, false);
    }
  }, [automation, projectId, setProjectId]);

  useEffect(() => {
    if (automationError?.status === 404) {
      navigate('/automations');
      notifications.show({
        title: 'Automation Not Found',
        message: 'The requested automation does not exist.',
        color: 'yellow',
      });
    }
  }, [automationError, navigate]);

  if (!automation) return <CustomSpinner />;

  return (
    <AutomationProvider automation={automation}>
      {!E2E_AUTH_BYPASS && <AutomationSSE />}
      <EditVersionProvider>
        <StepProvider>
          <Outlet />
          {datasetModalId && (
            <LazyLoadErrorBoundary fullScreen resetKey={datasetModalId}>
              <Suspense fallback={<CustomSpinner />}>
                <DatasetViewerModal />
              </Suspense>
            </LazyLoadErrorBoundary>
          )}
        </StepProvider>
      </EditVersionProvider>
    </AutomationProvider>
  );
}
